
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Form, Message } from "element-ui";
import { cloneDeep } from "lodash";
import { IAddShippingModel, IUserModel } from "@/api/types";
import {
  createFactoryLoadingPlan,
  defaultFactoryLoadingPlanData,
} from "@/api/factory-loading-plans";
import { getCustomer } from "@/api/customer";
import { getUsersByJobPosition } from "@/api/users";
import { getShipping } from "@/api/shipping";
import { COMPANY_OPTIONS } from "@/constant";
import {
  highlightKeywords,
  matchesAllKeywords,
} from "@/utils/keyword-search";

interface CreateForm {
  companyCode: string | null;
  customerCode: string | null;
  customerId: string | null;
  customerAbbreviation: string | null;
  estimatedSailingSchedule: any;
  logisticsClerkId: string | null;
  sellerId: string | null;
  declaration: boolean | null;
  combinedContainer: boolean | null;
  shippingOrderId: string | null;
}

@Component({
  name: "CreateFactoryLoadingPlanDialog",
})
export default class CreateFactoryLoadingPlanDialog extends Vue {
  @Prop({ required: true }) private visible!: boolean;

  private companyCodeOptions = COMPANY_OPTIONS;
  private sellerList: IUserModel[] = [];
  private logisticsClerkList: IUserModel[] = [];
  private shippingOrderList: IAddShippingModel[] = [];
  private keyword = "";
  private listLoading = false;
  private customerLoading = false;
  private submitting = false;
  private usersLoaded = false;
  /** 最近一次检索成功并绑定的客户编号 */
  private boundCustomerCode: string | null = null;

  private form: CreateForm = this.emptyForm();

  private rules = {
    companyCode: [{ required: true, message: "请选择公司", trigger: "change" }],
    customerCode: [
      { required: true, message: "请输入客户编码", trigger: "blur" },
    ],
    customerAbbreviation: [
      { required: true, message: "请检索并绑定客户", trigger: "change" },
    ],
    estimatedSailingSchedule: [
      { required: true, message: "请选择预计装柜日期", trigger: "change" },
    ],
  };

  get canLoadShipping(): boolean {
    return !!(this.form.customerId && this.form.companyCode);
  }

  get isCustomerBound(): boolean {
    return !!(this.form.customerId && this.boundCustomerCode);
  }

  get hasCustomerCodeDraft(): boolean {
    return !!(this.form.customerCode || "").trim();
  }

  get logisticsClerkName(): string {
    const id = this.form.logisticsClerkId;
    if (!id) return "";
    const found = this.logisticsClerkList.find((u) => u.id === id);
    return found ? found.name : "";
  }

  get sellerName(): string {
    const id = this.form.sellerId;
    if (!id) return "";
    const found = this.sellerList.find((u) => u.id === id);
    return found ? found.name : "";
  }

  get searchKeywords(): string[] {
    return (this.keyword || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  get filteredOrders(): IAddShippingModel[] {
    const keywords = this.searchKeywords.map((term) => term.toLowerCase());
    if (!keywords.length) return this.shippingOrderList;
    return this.shippingOrderList.filter((item) =>
      matchesAllKeywords([String(item.shippingCode || "")], keywords)
    );
  }

  get selectedOrder(): IAddShippingModel | null {
    if (!this.form.shippingOrderId) return null;
    return (
      this.shippingOrderList.find(
        (item) => item.id === this.form.shippingOrderId
      ) || null
    );
  }

  @Watch("visible")
  private onVisibleChange(val: boolean) {
    if (val) {
      this.resetForm();
      this.ensureUsersLoaded();
    }
  }

  private emptyForm(): CreateForm {
    const base = cloneDeep(defaultFactoryLoadingPlanData) as any;
    return {
      companyCode: base.companyCode,
      customerCode: null,
      customerId: base.customerId,
      customerAbbreviation: base.customerAbbreviation || base.customerAbbr,
      estimatedSailingSchedule: base.estimatedSailingSchedule,
      logisticsClerkId: base.logisticsClerkId,
      sellerId: base.sellerId,
      declaration: null,
      combinedContainer: null,
      shippingOrderId: null,
    };
  }

  private resetForm() {
    this.form = this.emptyForm();
    this.boundCustomerCode = null;
    this.keyword = "";
    this.shippingOrderList = [];
    this.$nextTick(() => {
      const formRef = this.$refs.dataForm as Form;
      if (formRef) formRef.clearValidate();
    });
  }

  private async ensureUsersLoaded() {
    if (this.usersLoaded) return;
    const [sellers, clerks]: any = await Promise.all([
      getUsersByJobPosition("seller"),
      getUsersByJobPosition("logistics-clerk"),
    ]);
    this.sellerList = sellers || [];
    this.logisticsClerkList = clerks || [];
    this.usersLoaded = true;
  }

  private onCustomerCodeInput() {
    const draft = (this.form.customerCode || "").trim();
    if (!this.boundCustomerCode) {
      if (!draft && this.form.customerId) {
        this.clearCustomerBinding();
      }
      return;
    }
    if (draft !== this.boundCustomerCode) {
      this.clearCustomerBinding();
    }
  }

  private clearCustomerBinding() {
    this.boundCustomerCode = null;
    this.form.customerId = null;
    this.form.customerAbbreviation = null;
    this.form.sellerId = null;
    this.form.logisticsClerkId = null;
    this.form.shippingOrderId = null;
    this.shippingOrderList = [];
    this.keyword = "";
  }

  private async searchCustomer() {
    const customerCode = (this.form.customerCode || "").trim();
    if (!customerCode) {
      Message.warning("请输入客户编码");
      this.clearCustomerBinding();
      return;
    }
    this.form.customerCode = customerCode;
    this.customerLoading = true;
    try {
      const { id, abbreviation, seller, logistics }: any = await getCustomer(
        customerCode
      );
      this.boundCustomerCode = customerCode;
      this.form.customerId = id;
      this.form.customerAbbreviation = abbreviation;
      this.form.sellerId = seller;
      this.form.logisticsClerkId = logistics;
      this.form.shippingOrderId = null;
      this.shippingOrderList = [];
      this.keyword = "";
      this.$nextTick(() => {
        const formRef = this.$refs.dataForm as Form;
        if (formRef) formRef.clearValidate("customerAbbreviation");
      });
      if (
        this.form.declaration === true &&
        this.form.combinedContainer !== null
      ) {
        await this.loadShippingOrders();
      }
    } catch (e) {
      this.clearCustomerBinding();
    } finally {
      this.customerLoading = false;
    }
  }

  private onDeclarationChange(value: boolean) {
    this.form.declaration = value;
    if (!value) {
      this.form.combinedContainer = null;
      this.form.shippingOrderId = null;
      this.shippingOrderList = [];
      this.keyword = "";
    }
  }

  private async onCombinedChange(value: boolean) {
    if (!this.canLoadShipping) return;
    this.form.combinedContainer = value;
    this.form.shippingOrderId = null;
    this.keyword = "";
    await this.loadShippingOrders();
  }

  private onCompanyOrCustomerChange() {
    this.form.shippingOrderId = null;
    this.shippingOrderList = [];
    if (
      this.form.declaration === true &&
      this.form.combinedContainer !== null &&
      this.canLoadShipping
    ) {
      this.loadShippingOrders();
    }
  }

  private selectOrder(id: string) {
    this.form.shippingOrderId =
      this.form.shippingOrderId === id ? null : id;
  }

  private highlightShippingCode(text: string): string {
    return highlightKeywords(text, this.searchKeywords);
  }

  private async loadShippingOrders() {
    if (!this.canLoadShipping || this.form.combinedContainer === null) {
      this.shippingOrderList = [];
      return;
    }
    this.listLoading = true;
    try {
      const data: any = await getShipping({
        combinedContainer: this.form.combinedContainer,
        customerId: this.form.customerId,
        company: this.form.companyCode,
      });
      this.shippingOrderList = Array.isArray(data) ? data : [];
    } finally {
      this.listLoading = false;
    }
  }

  private handleClose() {
    this.$emit("update:visible", false);
  }

  private handleSubmit() {
    (this.$refs.dataForm as Form).validate(async (valid) => {
      if (!valid) return;
      if (!this.isCustomerBound) {
        Message.error("请先检索并绑定客户");
        return;
      }
      if (this.form.declaration === null || this.form.declaration === undefined) {
        Message.error("请选择是否报关");
        return;
      }

      this.submitting = true;
      try {
        const payload: any = {
          ...cloneDeep(defaultFactoryLoadingPlanData),
          companyCode: this.form.companyCode,
          customerCode: this.boundCustomerCode,
          customerId: this.form.customerId,
          customerAbbreviation: this.form.customerAbbreviation,
          estimatedSailingSchedule: this.form.estimatedSailingSchedule,
          logisticsClerkId: this.form.logisticsClerkId,
          sellerId: this.form.sellerId,
          declaration: this.form.declaration,
          combinedContainer:
            this.form.declaration === true
              ? this.form.combinedContainer
              : null,
          shippingOrderId:
            this.form.declaration === true
              ? this.form.shippingOrderId
              : null,
        };

        await createFactoryLoadingPlan(payload);
        this.$notify({
          title: "成功",
          message: "创建成功",
          type: "success",
          duration: 2000,
        });
        this.$emit("success");
        this.handleClose();
      } finally {
        this.submitting = false;
      }
    });
  }
}
